import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { Mail, Phone, PhoneOutgoing } from "lucide-react"
import { parseServerDate } from "@/lib/utils"
import { RueckrufEintragenKnopf } from "./RueckrufStatus"

/**
 * #162 (Marlene, 06.10.2026): „Zu diesem Sachverhalt habe ich die Kundin nun
 * persönlich am Telefon gesprochen — wie können wir zukünftig die Information
 * zum letzten Kontakt auch mit einem Blick in der Mail sehen?"
 *
 * Eine Zeile an der Mail: wann und wie zuletzt Kontakt zum Kunden war
 * (Telefonat über Sipgate oder KI Telefon, Mail, eigener Vermerk), die zwei
 * davor im Aufklapper. Dazu „Telefonat vermerken" — dieselbe Statusliste wie
 * „Rückruf eintragen" (#151), bleibt im Portal, geht nicht nach HERO.
 *
 * 🔴 Scheitert die Abfrage, steht das da — nie „kein Kontakt".
 */

export type KontaktEintrag = {
  art: "telefon" | "mail" | "vermerk" | string
  was: string
  zeit: string | null
  wer?: string | null
  text?: string | null
  ziel?: string | null
}

type Antwort = {
  eintraege: KontaktEintrag[]
  zuordnung: string | null
  unvollstaendig: boolean
}

function wann(iso?: string | null): string {
  const d = parseServerDate(iso)
  if (!d) return ""
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
}

function Symbol({ art }: { art: string }) {
  if (art === "mail") return <Mail className="w-3 h-3 shrink-0" />
  if (art === "vermerk") return <PhoneOutgoing className="w-3 h-3 shrink-0" />
  return <Phone className="w-3 h-3 shrink-0" />
}

function Eintrag({ e }: { e: KontaktEintrag }) {
  const kopf = (
    <>
      <Symbol art={e.art} />
      <span className="font-medium">{e.was}</span>
      <span className="text-muted-foreground">
        {[wann(e.zeit), e.wer].filter(Boolean).join(" · ")}
      </span>
    </>
  )
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0" title={e.text || undefined}>
      {e.ziel ? (
        <Link to={e.ziel} className="inline-flex items-center gap-1.5 hover:underline">{kopf}</Link>
      ) : kopf}
      {e.text && <span className="text-muted-foreground truncate max-w-[28ch]">— {e.text}</span>}
    </span>
  )
}

export function LetzterKontakt({ emailId }: { emailId: number }) {
  const qc = useQueryClient()
  const q = useQuery<Antwort>({
    queryKey: ["letzter-kontakt", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/letzter-kontakt`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 30_000,
    retry: false,
  })
  const neu = () => {
    qc.invalidateQueries({ queryKey: ["letzter-kontakt", emailId] })
    qc.invalidateQueries({ queryKey: ["mail-verlauf", emailId] })
  }
  if (q.isLoading) return null

  const [erster, ...davor] = q.data?.eintraege ?? []
  return (
    <div className="mt-2 text-[12px] flex flex-col gap-1" data-testid="letzter-kontakt">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-muted-foreground shrink-0">Letzter Kontakt:</span>
        {q.isError ? (
          <span className="text-destructive">konnte nicht geladen werden</span>
        ) : erster ? (
          <Eintrag e={erster} />
        ) : q.data?.unvollstaendig ? (
          <span className="text-muted-foreground">nicht vollständig geladen</span>
        ) : (
          <span className="text-muted-foreground">
            {q.data?.zuordnung ? "keiner gefunden" : "keiner gefunden (Kunde nicht zugeordnet)"}
          </span>
        )}
        {erster && q.data?.unvollstaendig && (
          <span className="text-muted-foreground" title="Eine Quelle war nicht erreichbar">(unvollständig)</span>
        )}
        <span className="ml-auto">
          <RueckrufEintragenKnopf
            callId={`mail:${emailId}`}
            url={`/api/v1/emails/${emailId}/telefonat`}
            label="Telefonat vermerken"
            onChange={neu}
          />
        </span>
      </div>
      {davor.length > 0 && (
        <details className="text-muted-foreground">
          <summary className="cursor-pointer w-fit">davor ({davor.length})</summary>
          <ul className="mt-1 ml-1 space-y-0.5">
            {davor.map((e, i) => <li key={i}><Eintrag e={e} /></li>)}
          </ul>
        </details>
      )}
    </div>
  )
}
